import React, {
  useCallback, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, ChevronRight, CircleDollarSign, CreditCard, Globe2, Package,
  RotateCcw, TrendingUp,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import { loadCommerceAnalytics, loadCommerceEconomics, loadTransactions } from './api';
import { tx } from './i18n';
import { codeLabel, orderReferenceLabel } from './labels';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, WorkspaceTabs, XomicsSelect,
} from './parts';
import { useLiveResource } from './useLive';

const DAY_MS = 24 * 60 * 60 * 1000;
const PAYMENT_STATUSES = [
  'requires_payment_method', 'requires_confirmation', 'requires_action',
  'processing', 'requires_capture', 'canceled', 'succeeded',
];
const STATUS_TONES = {
  requires_payment_method: 'warn',
  requires_confirmation: 'warn',
  requires_action: 'warn',
  processing: 'info',
  requires_capture: 'info',
  canceled: 'bad',
  succeeded: 'ok',
};

function rangeFor(days) {
  const to = Date.now() + 1;
  return { from: to - (days * DAY_MS), to };
}

function money(amountMinor, currency, minorUnit) {
  const amount = Number(amountMinor) / (10 ** Number(minorUnit));
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol',
    }).format(amount);
  } catch (_error) {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

function customerName(customer) {
  return [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || '—';
}

function share(value, total) {
  return total > 0 ? Math.min(100, (Math.max(0, Number(value)) / total) * 100) : 0;
}

function percentage(value, total) {
  return total > 0 ? `${((Number(value) / Number(total)) * 100).toFixed(1)}%` : '0.0%';
}

function dayKey(value) {
  const date = new Date(Number(value));
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dayLabel(value) {
  return new Date(Number(value)).toLocaleDateString(undefined, {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
}

function timeLabel(value) {
  return new Date(Number(value)).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  });
}

function analyticsDayLabel(value) {
  return new Date(Number(value)).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

function CurrencyEconomics({ row }) {
  const transactionTotal = Math.max(0, Number(row.transactionCount));
  const outcomes = [
    {
      key: 'succeeded', label: tx('ui.economics.succeeded'),
      value: Number(row.succeededCount), radius: 66,
    },
    {
      key: 'pending', label: tx('ui.economics.pending'),
      value: Number(row.pendingCount), radius: 53,
    },
    {
      key: 'canceled', label: tx('ui.economics.canceled'),
      value: Number(row.canceledCount), radius: 40,
    },
  ];
  const composition = [
    { label: tx('ui.economics.subtotal'), value: Number(row.subtotalMinor) },
    { label: tx('ui.economics.shipping'), value: Number(row.shippingMinor) },
    { label: tx('ui.economics.tax'), value: Number(row.taxMinor) },
    { label: tx('ui.economics.discounts'), value: -Number(row.discountMinor) },
  ];

  return (
    <section className="xo-economics-statement">
      <header className="xo-economics-statement-head">
        <div className="xo-economics-net">
          <span>{row.currency} · {row.currencyName}</span>
          <small>{tx('ui.economics.net_collected')}</small>
          <strong>{money(row.netCollectedMinor, row.currency, row.minorUnit)}</strong>
          <p>{tx('ui.economics.currency_subtitle', { count: row.transactionCount })}</p>
        </div>
        <dl className="xo-economics-balance">
          <div><dt>{tx('ui.economics.booked_total')}</dt><dd>{money(row.bookedTotalMinor, row.currency, row.minorUnit)}</dd></div>
          <div><dt>{tx('ui.economics.collected')}</dt><dd>{money(row.collectedMinor, row.currency, row.minorUnit)}</dd></div>
          <div><dt>{tx('ui.economics.refunded')}</dt><dd>{money(row.refundedMinor, row.currency, row.minorUnit)}</dd></div>
          <div><dt>{tx('ui.economics.paid_orders')}</dt><dd>{Number(row.paidOrderCount).toLocaleString()}</dd></div>
        </dl>
      </header>

      <div className="xo-economics-statement-body">
        <figure className="xo-economics-orbits">
          <div className="xo-economics-orbit-graphic">
            <svg viewBox="0 0 180 180" role="img" aria-label={tx('ui.transactions.status')}>
              {outcomes.map((outcome) => (
                <g key={outcome.key} className={`is-${outcome.key}`}>
                  <circle className="xo-economics-orbit-track" cx="90" cy="90" r={outcome.radius} />
                  <circle className="xo-economics-orbit-value" cx="90" cy="90" r={outcome.radius}
                    pathLength="100"
                    strokeDasharray={`${share(outcome.value, transactionTotal)} 100`} />
                </g>
              ))}
            </svg>
            <span><strong>{transactionTotal.toLocaleString()}</strong><small>{tx('ui.nav.transactions')}</small></span>
          </div>
          <figcaption>
            <span>{tx('ui.transactions.status')}</span>
            <dl>
              {outcomes.map((outcome) => (
                <div className={`is-${outcome.key}`} key={outcome.key}>
                  <dt><i aria-hidden="true" />{outcome.label}</dt>
                  <dd>{outcome.value.toLocaleString()}</dd>
                </div>
              ))}
            </dl>
          </figcaption>
        </figure>

        <section className="xo-economics-receipt">
          <header>
            <span>{tx('ui.economics.booked_total')}</span>
            <strong>{money(row.bookedTotalMinor, row.currency, row.minorUnit)}</strong>
          </header>
          <dl>
            {composition.map((item) => (
              <div key={item.label}>
                <dt><span>{item.label}</span></dt>
                <dd>{item.value < 0 ? '−' : ''}{money(Math.abs(item.value), row.currency, row.minorUnit)}</dd>
              </div>
            ))}
          </dl>
          <footer>
            <span>{tx('ui.economics.net_collected')}</span>
            <strong>{money(row.netCollectedMinor, row.currency, row.minorUnit)}</strong>
          </footer>
        </section>
      </div>
    </section>
  );
}

function denseDailySeries(rows, from, to) {
  const start = Math.floor(Number(from) / DAY_MS) * DAY_MS;
  const end = Math.floor((Number(to) - 1) / DAY_MS) * DAY_MS;
  const byDay = new Map((rows || []).map((row) => [Number(row.at), row]));
  const result = [];
  for (let at = start; at <= end; at += DAY_MS) {
    result.push(byDay.get(at) || {
      at, attempts: 0, succeeded: 0, pending: 0, canceled: 0,
      collectedMinor: 0, refundedMinor: 0, netCollectedMinor: 0,
    });
  }
  return result;
}

function RevenueTrend({ report }) {
  const series = denseDailySeries(report.daily, report.from, report.to);
  const width = 900;
  const height = 260;
  const horizontalPadding = 22;
  const verticalPadding = 24;
  const max = Math.max(1, ...series.map((day) => Number(day.collectedMinor)));
  const point = (day, index, field) => ({
    x: horizontalPadding + ((index / Math.max(1, series.length - 1))
      * (width - (horizontalPadding * 2))),
    y: height - verticalPadding
      - ((Number(day[field]) / max) * (height - (verticalPadding * 2))),
  });
  const collected = series.map((day, index) => point(day, index, 'collectedMinor'));
  const net = series.map((day, index) => point(day, index, 'netCollectedMinor'));
  const line = (points) => points.map((item, index) => (
    `${index === 0 ? 'M' : 'L'} ${item.x.toFixed(2)} ${item.y.toFixed(2)}`
  )).join(' ');
  const netArea = net.length === 0 ? ''
    : `M ${net[0].x.toFixed(2)} ${(height - verticalPadding).toFixed(2)} ${line(net).replace(/^M/, 'L')} L ${net[net.length - 1].x.toFixed(2)} ${(height - verticalPadding).toFixed(2)} Z`;
  const first = series[0]?.at;
  const last = series[series.length - 1]?.at;

  return (
    <figure className="xo-commerce-trend">
      <header className="xo-section-heading">
        <div>
          <h2>{tx('ui.economics.revenue_over_time')}</h2>
          <p>{tx('ui.economics.revenue_trend_detail')}</p>
        </div>
        <div className="xo-commerce-trend-key">
          <span className="is-collected">{tx('ui.economics.collected_line')}</span>
          <span className="is-net">{tx('ui.economics.net_line')}</span>
        </div>
      </header>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none"
        role="img" aria-label={tx('ui.economics.revenue_over_time')}>
        {[0, 1, 2, 3, 4].map((step) => {
          const y = verticalPadding + ((step / 4) * (height - (verticalPadding * 2)));
          return <line key={step} className="xo-commerce-trend-grid" x1={horizontalPadding} y1={y} x2={width - horizontalPadding} y2={y} />;
        })}
        {netArea && <path className="xo-commerce-trend-area" d={netArea} />}
        {collected.length > 0 && <path className="xo-commerce-trend-collected" d={line(collected)} />}
        {net.length > 0 && <path className="xo-commerce-trend-net" d={line(net)} />}
      </svg>
      <figcaption>
        <time>{first === undefined ? '—' : analyticsDayLabel(first)}</time>
        <strong>{money(max, report.currency.code, report.currency.minorUnit)}</strong>
        <time>{last === undefined ? '—' : analyticsDayLabel(last)}</time>
      </figcaption>
    </figure>
  );
}

function AnalyticsMetrics({ report }) {
  const { totals, currency } = report;
  const metrics = [
    {
      label: tx('ui.economics.net_collected'),
      value: money(totals.netCollectedMinor, currency.code, currency.minorUnit),
      detail: tx('ui.finance.orders_count', {
        count: Number(totals.succeeded).toLocaleString(),
      }), Icon: TrendingUp,
    },
    {
      label: tx('ui.economics.average_order'),
      value: money(totals.averageOrderMinor, currency.code, currency.minorUnit),
      detail: `${Number(totals.paidOrders).toLocaleString()} ${tx('ui.economics.orders')}`, Icon: Package,
    },
    {
      label: tx('ui.economics.collected'),
      value: money(totals.collectedMinor, currency.code, currency.minorUnit),
      detail: tx('ui.finance.refunded_part', { amount: money(totals.refundedMinor, currency.code, currency.minorUnit) }), Icon: Activity,
    },
    {
      label: tx('ui.economics.refund_rate'),
      value: percentage(totals.refundedMinor, totals.collectedMinor),
      detail: money(totals.refundedMinor, currency.code, currency.minorUnit), Icon: RotateCcw,
    },
    {
      label: tx('ui.economics.coupon_adoption'),
      value: percentage(totals.discountedOrders, totals.paidOrders),
      detail: money(totals.discountMinor, currency.code, currency.minorUnit), Icon: CircleDollarSign,
    },
  ];
  return (
    <dl className="xo-commerce-analytics-metrics">
      {metrics.map(({ label, value, detail, Icon }) => (
        <div key={label}>
          <Icon aria-hidden="true" />
          <dt>{label}</dt>
          <dd>{value}</dd>
          <small>{detail}</small>
        </div>
      ))}
    </dl>
  );
}

function MarketDistribution({ report }) {
  const max = Math.max(1, ...report.markets.map((market) => market.netCollectedMinor));
  return (
    <section className="xo-commerce-analysis-panel xo-commerce-markets">
      <header className="xo-section-heading">
        <div><h2>{tx('ui.economics.market_distribution')}</h2><p>{tx('ui.economics.market_detail')}</p></div>
        <Globe2 aria-hidden="true" />
      </header>
      {report.markets.length === 0 ? (
        <Empty title={tx('ui.economics.no_market_data')} />
      ) : (
        <div className="xo-commerce-market-field">
          {report.markets.map((market) => {
            const scale = Math.sqrt(Math.max(0, market.netCollectedMinor) / max);
            return (
              <div key={market.countryCode}
                style={{ '--xo-market-size': `${86 + (scale * 76)}px`, '--xo-market-depth': 0.1 + (scale * 0.15) }}>
                <strong>{market.countryCode}</strong>
                <span>{money(market.netCollectedMinor, report.currency.code, report.currency.minorUnit)}</span>
                <small>{market.orderCount.toLocaleString()} {tx('ui.economics.orders')}</small>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ProductPerformance({ report }) {
  return (
    <section className="xo-commerce-analysis-panel xo-commerce-products">
      <header className="xo-section-heading">
        <div><h2>{tx('ui.economics.product_performance')}</h2><p>{tx('ui.economics.product_detail')}</p></div>
        <Package aria-hidden="true" />
      </header>
      {report.products.length === 0 ? (
        <Empty title={tx('ui.economics.no_product_data')} />
      ) : (
        <ol>
          {report.products.map((product, index) => (
            <li key={product.code}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><strong>{product.name}</strong><small>{product.code}</small></div>
              <dl>
                <div><dt>{tx('ui.economics.units')}</dt><dd>{product.units.toLocaleString()}</dd></div>
                <div><dt>{tx('ui.economics.orders')}</dt><dd>{product.orderCount.toLocaleString()}</dd></div>
              </dl>
              <strong>{money(product.productSalesMinor, report.currency.code, report.currency.minorUnit)}</strong>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function TransactionWindow({ economics, selectedCurrency, filteredTotal }) {
  const currencies = economics?.currencies || [];
  const visible = selectedCurrency
    ? currencies.filter((entry) => entry.currency === selectedCurrency)
    : currencies;
  const total = (field) => visible.reduce((sum, entry) => sum + Number(entry[field]), 0);
  const attempts = total('transactionCount');
  const stats = [
    { label: tx('ui.transactions.completed'), value: total('succeededCount') },
    { label: tx('ui.transactions.unresolved'), value: total('pendingCount') },
    { label: tx('ui.economics.canceled'), value: total('canceledCount') },
  ];
  return (
    <section className="xo-transaction-window">
      <div className="xo-transaction-window-total">
        <span>{tx('ui.transactions.filtered_total')}</span>
        <strong>{filteredTotal === undefined ? '—' : Number(filteredTotal).toLocaleString()}</strong>
        <small>{selectedCurrency
          ? tx('ui.transactions.window_one_market', { currency: selectedCurrency })
          : tx('ui.transactions.window_all_markets', { count: currencies.length })}</small>
      </div>
      <dl>
        {stats.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt><dd>{item.value.toLocaleString()}</dd>
            <small>{percentage(item.value, attempts)}</small>
          </div>
        ))}
      </dl>
    </section>
  );
}

function TransactionLedger({
  rows, loading, error, onRetry, compact = false, orderScope = null,
}) {
  if (loading && rows === null) return <Loading label={tx('ui.transactions.loading')} />;
  if (error) return <Failed message={error} onRetry={onRetry} />;
  if (!rows || rows.length === 0) {
    return <Empty title={tx('ui.transactions.empty')} detail={tx('ui.transactions.empty_detail')} />;
  }
  const groups = [];
  rows.forEach((row) => {
    const key = dayKey(row.createdAt);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = { key, createdAt: row.createdAt, rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  });
  return (
    <div className={`xo-transaction-ledger${compact ? ' is-compact' : ''}`}>
      {groups.map((group) => (
        <section className="xo-transaction-day" key={group.key}>
          <header><time dateTime={new Date(Number(group.createdAt)).toISOString()}>{dayLabel(group.createdAt)}</time>
            <span>{group.rows.length.toLocaleString()}</span></header>
          <ol>{group.rows.map((row) => {
            const tone = STATUS_TONES[row.status] || 'flat';
            const orderPath = orderScope !== null && row.orderUuid
              ? `/xomics/orders?scope=${encodeURIComponent(orderScope)}&orderUuid=${encodeURIComponent(row.orderUuid)}`
              : null;
            const contents = (
              <>
                <span className="xo-transaction-marker" aria-hidden="true"><CreditCard /></span>
                <div className="xo-transaction-reference">
                  <strong>{orderReferenceLabel(row.orderReference)}</strong>
                  <small>{row.providerPaymentId || tx('ui.transactions.payment_intent_pending')}</small>
                </div>
                <div className="xo-transaction-customer">
                  <strong>{customerName(row.customer)}</strong>
                  <small>{row.source}</small>
                </div>
                <div className="xo-transaction-market">
                  <strong>{row.customer?.countryCode || '—'} · {row.currency}</strong>
                  <small>{row.provider}</small>
                </div>
                <div className="xo-transaction-state">
                  {compact
                    ? <span className="xo-transaction-compact-status">{codeLabel('paymentStatus', row.status)}</span>
                    : <Pill tone={tone}>{codeLabel('paymentStatus', row.status)}</Pill>}
                  {row.failureCode && <small className="t-bad">{row.failureCode}</small>}
                </div>
                <div className="xo-transaction-amount">
                  <strong>{money(row.amountMinor, row.currency, row.minorUnit)}</strong>
                  {Number(row.refundedMinor) > 0 && (
                    <small>{tx('ui.transactions.refund')} −{money(row.refundedMinor, row.currency, row.minorUnit)}</small>
                  )}
                </div>
                <time className="xo-transaction-time" dateTime={new Date(Number(row.createdAt)).toISOString()}>
                  {timeLabel(row.createdAt)}
                </time>
                {orderPath !== null && <ChevronRight className="xo-transaction-open" aria-hidden="true" />}
              </>
            );
            return orderPath === null ? (
              <li className={`xo-transaction-entry is-${tone}`} key={row.id}>
                {contents}
              </li>
            ) : (
              <li className="xo-transaction-row" key={row.id}>
                <Link className={`xo-transaction-entry is-${tone} is-linked`} to={orderPath}
                  aria-label={`${orderReferenceLabel(row.orderReference)} · ${customerName(row.customer)}`}>
                  {contents}
                </Link>
              </li>
            );
          })}</ol>
        </section>
      ))}
    </div>
  );
}

export default function XomicsTransactions({ view = 'transactions' }) {
  const { permissions } = useXomicsAuth();
  const orderScope = permissions.includes('ORDER_READ')
    ? (permissions.includes('ORDER_READ_ALL') ? 'global' : 'local')
    : null;
  const [days, setDays] = useState(30);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [status, setStatus] = useState('');
  const [currency, setCurrency] = useState('');
  const [queryDraft, setQueryDraft] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState([null]);
  const pageSize = view === 'overview' ? 10 : 50;

  const loadEconomics = useCallback(
    () => loadCommerceEconomics(rangeFor(days)), [days],
  );
  const economics = useLiveResource(['orders'], loadEconomics);
  const beforeId = cursors[page] ?? null;
  const loadLedger = useCallback(() => loadTransactions({
    ...rangeFor(days), status, currency, q: query, limit: pageSize, beforeId,
  }), [beforeId, currency, days, pageSize, query, status]);
  const ledger = useLiveResource(['orders'], loadLedger, { enabled: view !== 'analytics' });
  const rows = ledger.data?.transactions ?? null;

  const currencyOptions = useMemo(
    () => (economics.data?.currencies || []).map((entry) => entry.currency),
    [economics.data],
  );
  const activeEconomics = useMemo(() => {
    const currencies = economics.data?.currencies || [];
    return currencies.find((entry) => entry.currency === selectedCurrency) || currencies[0] || null;
  }, [economics.data, selectedCurrency]);
  const analyticsCurrency = activeEconomics?.currency || '';
  const loadAnalytics = useCallback(() => loadCommerceAnalytics({
    ...rangeFor(days), currency: analyticsCurrency,
  }), [analyticsCurrency, days]);
  const analytics = useLiveResource(['orders'], loadAnalytics, {
    enabled: view === 'analytics' && analyticsCurrency !== '',
  });

  function resetPage() {
    setPage(0);
    setCursors([null]);
  }

  function changeDays(next) {
    setDays(next);
    resetPage();
  }

  function applySearch(event) {
    event.preventDefault();
    setQuery(queryDraft.trim());
    resetPage();
  }

  function clearFilters() {
    setStatus('');
    setCurrency('');
    setQueryDraft('');
    setQuery('');
    resetPage();
  }

  function nextPage() {
    const next = ledger.data?.nextBeforeId;
    if (next === null || next === undefined) return;
    setCursors((current) => {
      const copy = current.slice(0, page + 1);
      copy.push(next);
      return copy;
    });
    setPage((current) => current + 1);
  }

  const rangeControl = (
    <WorkspaceTabs className="xo-economics-range-tabs"
      value={days} onChange={changeDays} label={tx('ui.transactions.day_window')} tabs={[
      { value: 7, label: tx('ui.economics.days', { count: 7 }) },
      { value: 30, label: tx('ui.economics.days', { count: 30 }) },
      { value: 90, label: tx('ui.economics.days', { count: 90 }) },
      { value: 365, label: tx('ui.economics.year') },
    ]} />
  );

  if (view === 'overview') {
    return (
      <div className="xo-platform-page xo-economics-page">
        <Head title={tx('ui.economics.title')} subtitle={tx('ui.economics.subtitle')}>
          {rangeControl}
        </Head>
        {economics.loading && economics.data === null ? (
          <Loading label={tx('ui.economics.loading')} />
        ) : economics.error ? (
          <Failed message={economics.error} onRetry={economics.refresh} />
        ) : (economics.data?.currencies || []).length === 0 ? (
          <Empty title={tx('ui.economics.empty')} detail={tx('ui.economics.empty_detail')} />
        ) : (
          <>
            <WorkspaceTabs className="xo-economics-currency-tabs"
              value={activeEconomics?.currency || ''} onChange={setSelectedCurrency}
              label={tx('ui.transactions.currency')}
              tabs={economics.data.currencies.map((entry) => ({
                value: entry.currency,
                label: entry.currency,
                count: money(entry.netCollectedMinor, entry.currency, entry.minorUnit),
              }))} />
            {activeEconomics && <CurrencyEconomics row={activeEconomics} />}
          </>
        )}
        <section className="xo-economics-recent">
          <header className="xo-section-heading">
            <div><h2>{tx('ui.transactions.recent')}</h2><p>{tx('ui.transactions.recent_subtitle')}</p></div>
          </header>
          <TransactionLedger compact rows={rows} loading={ledger.loading}
            error={ledger.error} onRetry={ledger.refresh} orderScope={orderScope} />
        </section>
      </div>
    );
  }

  if (view === 'analytics') {
    const analyticsIsCurrent = analytics.data?.currency?.code === analyticsCurrency;
    return (
      <div className="xo-platform-page xo-commerce-analytics-page">
        <Head title={tx('ui.economics.analytics_title')} subtitle={tx('ui.economics.analytics_subtitle')}>
          {rangeControl}
        </Head>
        {economics.loading && economics.data === null ? (
          <Loading label={tx('ui.economics.loading')} />
        ) : economics.error ? (
          <Failed message={economics.error} onRetry={economics.refresh} />
        ) : currencyOptions.length === 0 ? (
          <Empty title={tx('ui.economics.empty')} detail={tx('ui.economics.empty_detail')} />
        ) : (
          <>
            <WorkspaceTabs className="xo-economics-currency-tabs"
              value={analyticsCurrency} onChange={setSelectedCurrency}
              label={tx('ui.transactions.currency')}
              tabs={currencyOptions.map((entry) => ({ value: entry, label: entry }))} />
            {analytics.loading && !analyticsIsCurrent ? (
              <Loading label={tx('ui.economics.analytics_loading')} />
            ) : analytics.error ? (
              <Failed message={analytics.error} onRetry={analytics.refresh} />
            ) : analyticsIsCurrent ? (
              <>
                <AnalyticsMetrics report={analytics.data} />
                <RevenueTrend report={analytics.data} />
                <div className="xo-commerce-analysis-split">
                  <MarketDistribution report={analytics.data} />
                  <ProductPerformance report={analytics.data} />
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="xo-platform-page xo-transactions-page">
      <Head title={tx('ui.transactions.title')} subtitle={tx('ui.transactions.subtitle')}>
        {rangeControl}
      </Head>
      <TransactionWindow economics={economics.data} selectedCurrency={currency}
        filteredTotal={ledger.data?.filteredTotal} />
      <div className="xo-bar xo-transactions-filters">
        <form className="xo-transaction-search" onSubmit={applySearch}>
          <SearchField value={queryDraft} onChange={setQueryDraft}
            placeholder={tx('ui.transactions.search_placeholder')}
            label={tx('ui.transactions.search_placeholder')} />
          <button type="submit" className="xo-btn-2">{tx('ui.transactions.search')}</button>
        </form>
        <div className="xo-bar-group">
          <label>{tx('ui.transactions.status')}
            <XomicsSelect value={status} onChange={(event) => { setStatus(event.target.value); resetPage(); }}>
              <option value="">{tx('ui.transactions.all_statuses')}</option>
              {PAYMENT_STATUSES.map((value) => <option key={value} value={value}>{codeLabel('paymentStatus', value)}</option>)}
            </XomicsSelect>
          </label>
          <label>{tx('ui.transactions.currency')}
            <XomicsSelect value={currency} onChange={(event) => { setCurrency(event.target.value); resetPage(); }}>
              <option value="">{tx('ui.transactions.all_currencies')}</option>
              {currencyOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </XomicsSelect>
          </label>
          <button type="button" className="xo-link" onClick={clearFilters}><RotateCcw aria-hidden="true" />{tx('ui.transactions.clear')}</button>
        </div>
      </div>
      <section className="xo-transaction-ledger-surface">
        <header className="xo-transaction-ledger-heading">
          <div><h2>{tx('ui.transactions.ledger_title')}</h2><p>{tx('ui.transactions.ledger_detail')}</p></div>
          <span>{status ? codeLabel('paymentStatus', status) : tx('ui.transactions.all_statuses')}</span>
        </header>
        <div className="xo-transaction-column-head" aria-hidden="true">
          <span>{tx('ui.transactions.date')}</span><i />
          <span>{tx('ui.transactions.reference')}</span>
          <span>{tx('ui.transactions.customer')}</span>
          <span>{tx('ui.transactions.market')}</span>
          <span>{tx('ui.transactions.status')}</span>
          <span>{tx('ui.transactions.amount')}</span>
          <span>{tx('ui.transactions.created')}</span><i />
        </div>
        <TransactionLedger rows={rows} loading={ledger.loading} error={ledger.error}
          onRetry={ledger.refresh} orderScope={orderScope} />
      </section>
      <div className="xo-foot-split">
        <span><CreditCard aria-hidden="true" /> {tx('ui.transactions.page', { page: page + 1 })}</span>
        <div className="xo-bar-group">
          <button type="button" className="xo-link" disabled={page === 0 || ledger.loading}
            onClick={() => setPage((current) => Math.max(0, current - 1))}>{tx('ui.ui.previous_50f94286')}</button>
          <button type="button" className="xo-link"
            disabled={!ledger.data?.nextBeforeId || ledger.loading} onClick={nextPage}>{tx('ui.ui.next_bc981983')}</button>
        </div>
      </div>
      {query && <p className="xo-transaction-query"><CircleDollarSign aria-hidden="true" />{tx('ui.transactions.searching_for', { query })}</p>}
    </div>
  );
}
